import { userUiPreferenceStorage } from "../lib/userUiPreferenceStorage.js";
const DATA_MANAGER_TABLE_SORT_STORAGE_KEY = "grain-saas:data-manager:table-sort:v1";

const memoryTableSortSnapshot = {};

function getWindowSessionStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return userUiPreferenceStorage("session");
  } catch {
    return null;
  }
}

function resolveTableSortStorage(storage = null) {
  if (
    storage
    && typeof storage.getItem === "function"
    && typeof storage.setItem === "function"
    && typeof storage.removeItem === "function"
  ) {
    return storage;
  }

  return getWindowSessionStorage();
}

function parseStorageSnapshot(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(value);
    return parsedValue && typeof parsedValue === "object" && !Array.isArray(parsedValue)
      ? parsedValue
      : null;
  } catch {
    return null;
  }
}

export function normalizeDataManagerTableSortState(sortState = null) {
  const normalizedKey = typeof sortState?.key === "string" && sortState.key.trim()
    ? sortState.key.trim()
    : null;
  const normalizedDirection = sortState?.direction === "desc" ? "desc" : "asc";

  return {
    direction: normalizedDirection,
    key: normalizedKey,
  };
}

function readTableSortSnapshot(storage = null) {
  const resolvedStorage = resolveTableSortStorage(storage);

  if (resolvedStorage) {
    try {
      const rawSnapshot = resolvedStorage.getItem(DATA_MANAGER_TABLE_SORT_STORAGE_KEY);
      const parsedSnapshot = parseStorageSnapshot(rawSnapshot);
      if (parsedSnapshot) {
        return parsedSnapshot;
      }
    } catch {
      return { ...memoryTableSortSnapshot };
    }
  }

  return { ...memoryTableSortSnapshot };
}

function writeTableSortSnapshot(snapshot, storage = null) {
  const resolvedStorage = resolveTableSortStorage(storage);
  Object.keys(memoryTableSortSnapshot).forEach((tableKey) => {
    delete memoryTableSortSnapshot[tableKey];
  });
  Object.assign(memoryTableSortSnapshot, snapshot);

  if (!resolvedStorage) {
    return;
  }

  try {
    resolvedStorage.setItem(DATA_MANAGER_TABLE_SORT_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage is best-effort only.
  }
}

export function readDataManagerTableSortState(tableKey, storage = null) {
  const normalizedTableKey = typeof tableKey === "string" ? tableKey.trim() : "";
  if (!normalizedTableKey) {
    return null;
  }

  const snapshot = readTableSortSnapshot(storage);
  const persistedSortState = snapshot[normalizedTableKey];
  return persistedSortState ? normalizeDataManagerTableSortState(persistedSortState) : null;
}

export function resolveDataManagerTableSortState(tableKey, fallbackSortState, storage = null) {
  const persistedSortState = readDataManagerTableSortState(tableKey, storage);
  if (persistedSortState) {
    return persistedSortState;
  }

  return normalizeDataManagerTableSortState(fallbackSortState);
}

export function persistDataManagerTableSortState(tableKey, sortState, storage = null) {
  const normalizedTableKey = typeof tableKey === "string" ? tableKey.trim() : "";
  if (!normalizedTableKey) {
    return normalizeDataManagerTableSortState(sortState);
  }

  const normalizedSortState = normalizeDataManagerTableSortState(sortState);
  const nextSnapshot = {
    ...readTableSortSnapshot(storage),
    [normalizedTableKey]: normalizedSortState,
  };
  writeTableSortSnapshot(nextSnapshot, storage);
  return normalizedSortState;
}

export function clearDataManagerTableSortState(tableKey = "", storage = null) {
  const normalizedTableKey = typeof tableKey === "string" ? tableKey.trim() : "";
  if (!normalizedTableKey) {
    return;
  }

  const nextSnapshot = readTableSortSnapshot(storage);
  delete nextSnapshot[normalizedTableKey];
  writeTableSortSnapshot(nextSnapshot, storage);
}

export { DATA_MANAGER_TABLE_SORT_STORAGE_KEY };
